import './AdsAnnouncementPopup.scss';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../contexts/account/AccountContexts';
import { ADS } from '../../../routes/path';
import AnnouncementPopup from '../announcement-popup/AnnouncementPopup';
import { useAnnouncement } from '../announcement-popup/useAnnouncement';
import { projectTabPath } from '../announcement-popup/projectTabPath';

// Per-user "seen" entry in the `onboarding` table (same store as FirstProjectOnboarding) — no schema or
// endpoint change. Shell, visibility flow and CTA path are shared with InsightsAnnouncementPopup
// (`announcement-popup/`).
const ENTRY = 'ads-tab-announcement';

// Spec: the announcement targets accounts that already committed — paid, or trials past their
// exploration phase. Younger trials see nothing (and nothing is persisted, so they become eligible
// the moment they cross the threshold or upgrade).
const TRIAL_MIN_AGE_DAYS = 14;

// Decorative preview only — static shape, not the account's data.
const BAR_HEIGHTS = [40, 60, 46, 76, 62, 96, 66, 116, 80, 100, 70, 122];
const ACCENT_BARS = 3;

const AdsAnnouncementPopup: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();

  // The announcement is about the account's own entitlement, so it follows the logged-in account's
  // addon — deliberately without the super-admin bypass the module gate has (support gets no promo).
  const hasAdsAddon = !!accountResponse?.addons?.includes('ADS_STATS');
  const isOnAdsPage = location.pathname === ADS || location.pathname.startsWith(`${ADS}/`);
  // Account age from `created`, like getTrialDays (the trial has no explicit start date field).
  const accountAgeDays = accountResponse
    ? Math.floor((Date.now() - new Date(accountResponse.created).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const isEligibleAccount =
    accountResponse?.accountStatus === 'PAID' ||
    (accountResponse?.accountStatus === 'TRIAL' && accountAgeDays > TRIAL_MIN_AGE_DAYS);

  const { visible, dismiss } = useAnnouncement({
    entry: ENTRY,
    enabled: hasAdsAddon,
    isOnTab: isOnAdsPage,
    canOpen: isEligibleAccount,
  });

  const explore = () => {
    dismiss();
    navigate(projectTabPath(ADS, location.pathname, projectResponse));
  };

  if (!hasAdsAddon || isOnAdsPage) {
    return null;
  }

  return (
    <AnnouncementPopup
      visible={visible}
      onDismiss={dismiss}
      onCta={explore}
      className="ads-announcement"
      badge={t('ads.announcement.badge')}
      title={
        <>
          {t('ads.announcement.titlePrefix')}{' '}
          <span className="announcement-popup__title-accent">{t('ads.announcement.titleAccent')}</span>
        </>
      }
      description={t('ads.announcement.desc')}
      laterLabel={t('ads.announcement.later')}
      ctaLabel={
        <>
          {t('ads.announcement.cta')}
          <span className="ico--right ico--xs" />
        </>
      }
    >
      <div className="ads-announcement__preview" aria-hidden="true">
        <div className="ads-announcement__preview-title">
          <span className="ico--chart-up ico--xs" />
          {t('ads.announcement.previewTitle')}
        </div>
        <div className="ads-announcement__bars">
          {BAR_HEIGHTS.map((height, index) => (
            <span
              key={index}
              className={`ads-announcement__bar${
                index >= BAR_HEIGHTS.length - ACCENT_BARS ? ' ads-announcement__bar--accent' : ''
              }`}
              style={{ height: `${height}px` }}
            />
          ))}
        </div>
        <div className="ads-announcement__stats">
          <div className="ads-announcement__stat">
            <strong>197</strong>
            <span>{t('ads.announcement.statAds')}</span>
          </div>
          <div className="ads-announcement__stat">
            <strong>34 / 60</strong>
            <span>{t('ads.announcement.statPrompts')}</span>
          </div>
          <div className="ads-announcement__stat">
            <strong>21</strong>
            <span>{t('ads.announcement.statAdvertisers')}</span>
          </div>
        </div>
      </div>
    </AnnouncementPopup>
  );
};

export default AdsAnnouncementPopup;
